import { type AxiosResponse } from 'axios';
import { get, http, post, postWithoutToken, put } from '@utilities';

export const sendOTP = async (data: Record<string, any>): Promise<AxiosResponse> =>
	await postWithoutToken('auth/lead/send_otp', data, { addSourceHeader: true });
export const verifyOTP = async (data: Record<string, any>): Promise<AxiosResponse> =>
	await postWithoutToken('auth/lead/verify_otp', data, { addSourceHeader: true });
export const uploadFloorPlan = async (data: Record<string, any>): Promise<AxiosResponse> =>
	await post('project/floor_plan', data);
export const activateDocument = async (data: Record<string, any>): Promise<AxiosResponse> =>
	await post(`document/activate`, data);
export const getPrediction = async (predictionId: string): Promise<AxiosResponse> =>
	// Status checks must fail promptly; the shared five-minute timeout is for long-running work.
	await http.get(`quotation/prediction/${predictionId}/status`, {
		data: {},
		timeout: 30000,
		timeoutErrorMessage: 'The server did not respond to the floor plan status check within 30 seconds. Please try again later.'
	});
export const getPreDesignQuotation = async (data: Record<string, any>): Promise<AxiosResponse> =>
	await post('/pre-design/quotation', data);
export const getDefaultQuotations = async (data: Record<string, any>): Promise<AxiosResponse> =>
	await post('/pre-design/quotation/default', data);
export const getPreDesignQuotationSavedState = async (projectId: number | string): Promise<AxiosResponse> =>
	await get(`/pre-design/quotation/saved-state/${projectId}`);
export const createQuotation = async (data): Promise<AxiosResponse> => await post(`quotation`, data);
export const getProjectDetails = async (projectId: string): Promise<AxiosResponse> =>
	await post(`project/${projectId}`);
export const getProjectActivities = async (projectID: string): Promise<AxiosResponse> =>
	await get(`scheduler/${projectID}/timeline`);
export const completeRejectProjectActivity = async (data: any, schedulerID: any): Promise<AxiosResponse> =>
	await put(`scheduler/${schedulerID}/client-action`, data);
export const uploadActivityDocuments = async (data, schedulerID: any): Promise<AxiosResponse> =>
	await put(`scheduler/${schedulerID}/customer/docs`, data);
export const fetchActivityDocuments = async (docIds: any): Promise<AxiosResponse> => await post(`document/all`, docIds);
export const getProjectCurrentActivity = async (projectID: string): Promise<AxiosResponse> =>
	await get(`scheduler/${projectID}/current_activity`);
export const floorPlanValidator = async (data: Record<string, any>): Promise<AxiosResponse> =>
	await post(`chat-gpt`, data);
export const uploadProjectDetails = async (data: Record<string, any>): Promise<AxiosResponse> =>
	await put(`project/floor_plan`, data);
export const getAllProjects = async (data: Record<string, any>): Promise<AxiosResponse> =>
	await post('project/all', data);
export const fetchProductCategories = async (data: Record<string, any>): Promise<AxiosResponse> =>
	await post(`pre-design/categories`, data);
export const fetch2DJson = async (predictionId: any, currentFloor): Promise<AxiosResponse> =>
	await get(`mapping/${predictionId}/2-d?floor=${currentFloor}`);
export const fetch3DJson = async (predictionId: any, currentFloor): Promise<AxiosResponse> =>
	await get(`mapping/${predictionId}/3-d?floor=${currentFloor}`);
export const updateMappingWalls = async (
	predictionId: string,
	body: {
		floor?: number;
		global_walls: unknown[];
		areas?: unknown[];
		global_openings?: unknown[];
		global_beams?: unknown[];
		global_columns?: unknown[];
	}
): Promise<AxiosResponse> => await put(`mapping/${encodeURIComponent(predictionId)}/walls`, body);
export const fetchLeadProjects = async (leadId: any): Promise<AxiosResponse> => await post(`project/lead/${leadId}`);
export const fetchAreaListDropdown = async (quotationID): Promise<AxiosResponse> => {
	if (!quotationID || quotationID === 'undefined' || quotationID === 'null') {
		throw new Error('A quotation must be created before its areas can be loaded.');
	}
	return await get(`quotation/${quotationID}/area`);
};
export const fetchPaymentSlips = async (approvalIds: number[]) => {
	const params = approvalIds.map(id => `approvalId=${id}`).join('&');
	const url = `/approval/payment/receipts?${params}`;
	return await get(url);
};
export const getLedgerData = async (projectId: string): Promise<AxiosResponse> => {
	return await get(`ledger/${projectId}`);
};
export const getAllPayments = async (data: Record<string, any>): Promise<AxiosResponse> =>
	await post('approval/payment/all', data);
export const fetchCustomerSlip = async (data: Record<string, any>): Promise<AxiosResponse> =>
	await post(`document/all`, data);
export const uploadMoreActivityDocs = async (approvalId: number, formData: any): Promise<AxiosResponse> =>
	await put(`approval/payment/${approvalId}/upload`, formData);
export const getTimelineNotifications = async (projectId: number | string): Promise<AxiosResponse> =>
	await get(`timeline/${projectId}/notifications`);

export const uploadWarrantyClaimDocuments = async (data: Record<string, any>): Promise<AxiosResponse> =>
	await post(`warranty-claim/upload-documents`, data);

export const checkWarrantyValidity = async (data: Record<string, any>): Promise<AxiosResponse> =>
	await post('warranty-claim/check-validity', data);

export const createWarrantyClaim = async (data: Record<string, any>): Promise<AxiosResponse> =>
	await post('warranty-claim', data);

export const getWarrantyClaim = async (warrantyClaimId: number): Promise<AxiosResponse> =>
	await get(`warranty-claim/${warrantyClaimId}`);

export const getAllWarrantyClaims = async (data: Record<string, any>): Promise<AxiosResponse> =>
	await post('warranty-claim/all', data);

export const getWarrantyClaimsByLeadId = async (leadId: number): Promise<AxiosResponse> =>
	await get(`warranty-claim/lead/${leadId}`);

// Warranty code details (scope, policies, etc.)
export const getWarrantyCodeDetails = async (data: { warrantyCodes: string[] }): Promise<AxiosResponse> =>
	await post('warranty/code', data);
export const getCutlistData = async (quotationID: any): Promise<AxiosResponse> =>
	await get(`quotation/${quotationID}/cutlist/all`);
export const getOptimiserData = async (cutlistData: any, projectId: any): Promise<AxiosResponse> =>
	await post(`quotation/cutlist/${projectId}/optimize`, { cutlist: cutlistData });
export const getOptimizedCutlistData = async (projectId: string): Promise<AxiosResponse> => {
	if (!projectId || String(projectId).toLowerCase() === 'undefined' || String(projectId).toLowerCase() === 'null') {
		throw new Error('Project id is required to fetch optimized cutlist');
	}
	return await get(`quotation/cutlist/${projectId}/optimized`);
};
export const getAssignedProjects = async (): Promise<AxiosResponse> => await get('project/assigned');
export const getBuilderStates = async (quotationId: any): Promise<AxiosResponse> =>
	await get(`/quotation/${quotationId}/builder-states`);
export const getPromoCodeHistory = async (): Promise<AxiosResponse> => await get('promo-code/history');
export const calculatePromoCodeAmt = async (data: Record<string, any>): Promise<AxiosResponse> =>
	await post('quotation/promo/amt', data);
export const markCutlistViewed = async (leadId: number | string, projectId: number | string): Promise<AxiosResponse> =>
	await put(`/pre-design/${leadId}/cutlist/view?projectId=${projectId}`);
export const markBomViewed = async (leadId: number | string, projectId: number | string): Promise<AxiosResponse> =>
	await put(`/pre-design/${leadId}/bom/view?projectId=${projectId}`);

export const syncPreDesignQuotationSavedState = async (
	projectId: number | string,
	changes: any[]
): Promise<AxiosResponse> =>
	await put(`/pre-design/quotation/saved-state/${projectId}`, {
		projectId: Number(projectId),
		changes
	});

export const update3DPaintColor = async (predictionId: string, data: Record<string, any>): Promise<AxiosResponse> =>
	await put(`mapping/${encodeURIComponent(predictionId)}/paint-color`, data);

/** Persist one mapped product's plan footprint after a user drag / nudge / kitchen reorder. */
export const updateProductFootprint = async (
	predictionId: string,
	body: {
		floor?: number;
		areaName: string;
		productName: string;
		coordKey: string;
		coordinates: Record<string, unknown>;
	}
): Promise<AxiosResponse> =>
	await put(`mapping/${encodeURIComponent(predictionId)}/product-footprint`, body);
